import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'utils/api/axios'
import AxiosUtils from 'utils/api/axiosUtils'
import UrlApis from 'utils/api/urlApis'
import './modalLogIn.css'
import Modal from '@airbus/components-react/components/Modal'
import Button from '@airbus/components-react/components/Button'
import IconButton from '@airbus/components-react/components/IconButton'
import { useForm, SubmitHandler, FormProvider } from 'react-hook-form'
import AuthContext from 'context/AuthContext'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import MY_LS from 'utils/myLittleStorage'
import LocalStorage from 'utils/myLocalStorage'
import { Message, MessageSeverity } from 'constants/message'
import tools from 'utils/helpers'
import { TFunction } from 'i18next'
import { Flex, ProgressBar } from '@airbus/components-react/components'
import { Lang } from 'context/LangContext'
import FormInput from 'components/FormFields/FormInput'
import LightDarkContext from 'context/LightDarkModeContext'
import { useNavigate } from 'react-router-dom'
import ImgBgLight from 'resources/images/logo_amsweb_light.png'
import ImgBgDark from 'resources/images/logo_amsweb_dark.png'

const PROP_QRCODE_IMG = 'qrUri'

const styleModal = {
  position: 'absolute',
  top: 'calc(50% - (var(--height-logo-customer) / 2))',
  left: '50%',
  transform: 'translate(-50%, calc((50% - (var(--height-logo-customer) / 2)) * -1))',
  width: 400,
  border: '2px solid #000',
  boxShadow: 24,
  p: 2,
}

type Props = {
  lang: Lang
  isOpened: boolean
  onLoggedIn: () => void
  onCloseModal: () => void
  onCancelModal: () => void
  onDisplayMessage: (m: Message) => void
}

type Inputs = {
  user: string
  password: string
  token: string
}

type PasswordFieldProps = {
  required: boolean
  showPassword: boolean
  isSsoActivated: boolean
  isSsoLoginOk: boolean
  setShowPassword: React.Dispatch<React.SetStateAction<boolean>>
  t: TFunction<'translation', undefined>
  rules: any
}

const ModalLogIn = ({ lang, isOpened, onLoggedIn, onCloseModal, onCancelModal, onDisplayMessage }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { isMfaActivated, isSsoActivated, isConnected, setIsConnected } = useContext(AuthContext)

  const [showPassword, setShowPassword] = useState(false)
  const [loginInProgress, setLoginInProgress] = useState(false)

  const [isMfaSoftLoginOk, setIsMfaSoftLoginOk] = useState(false)
  const [returnLogin1FA, setReturnLogin1FA] = useState(null)

  // ATTENTION: false leads to a manual login
  const [isSsoLoginOk, setIsSsoLoginOk] = useState(null)

  const methods = useForm<Inputs>({})
  const { mode } = useContext(LightDarkContext)

  const {
    handleSubmit,
    setValue,
    formState: { isLoading },
    reset,
  } = methods

  /**
   * Login is successful : initialize user's environment
   * @param {*} response
   */
  const postLoginSuccessful = (response: any) => {
    const userName = response?.data?.username
    const userDftCompany = response?.data?.userDftCompany
    const userDftStore = response?.data?.userDftCenter
    const userDftService = response?.data?.userDftService
    const userPrefs = response?.data?.prefs
    const permissions = response?.data?.permissions
    const levels = response?.data?.levels
    const routineMaintenanceParams = response?.data?.routineMaintenanceParams

    LocalStorage.clear()
    LocalStorage.put([LocalStorage.KEY_USER], userName)
    LocalStorage.put([LocalStorage.KEY_USER_COMPANY], userDftCompany)
    LocalStorage.put([LocalStorage.KEY_USER_STORE], userDftStore)
    LocalStorage.put([LocalStorage.KEY_USER_SERVICE], userDftService)
    LocalStorage.put([LocalStorage.KEY_PREFS], userPrefs)
    LocalStorage.put([LocalStorage.KEY_PERMISSIONS], permissions)
    LocalStorage.put([LocalStorage.KEY_LEVELS], levels)
    LocalStorage.put([LocalStorage.KEY_ROUTINE_MAINTENANCE_PARAMS], routineMaintenanceParams)

    // Clear CSRF locally to force requesting a new-one when needed (SINGIN, SIGNOUT, POST, PUT, etc. operations)
    MY_LS.clear()

    //Notify parent of login successful
    onLoggedIn()
  }

  const handleLogIn1FA: SubmitHandler<Inputs> = async (data) => {
    try {
      const urlToCall = tools.dformat(UrlApis.URL_SPECIAL_SIGNIN_POST_PARAMETERIZED, lang)
      console.debug('1FA-signing in: ' + urlToCall)
      const headerCompleted = await AxiosUtils.getHeader(AxiosUtils.HEADERS.HEADER_SIGN_IN_OUT)
      const response = await axios.post(
        encodeURI(urlToCall),
        JSON.stringify({ username: data?.user.toUpperCase(), password: data?.password }),
        {
          headers: headerCompleted,
          withCredentials: false,
        },
      )
      console.debug('1FA-signing in response received!')

      // Check abnormalities for MFA: isMfaActivated <> (response.data.qrUri available)
      if (response.data?.hasOwnProperty(PROP_QRCODE_IMG) && !isMfaActivated) {
        setLoginInProgress(false)
        throw {
          response: {
            data: {
              message: t('9510047'),
            },
          },
        }
      }

      setIsMfaSoftLoginOk(true)

      if (isMfaActivated) {
        setReturnLogin1FA(response.data)
      } else {
        // Normal login without MFA
        setIsConnected(true)
        postLoginSuccessful(response)
      }

      console.debug('1FA-signing in finished !')
      // Set timestamp in cache (to decide later on whether session has timed-out or not)
      LocalStorage.put([LocalStorage.KEY_TIMESTAMP_LAST_SERVER_CALL], new Date().getTime())

      setLoginInProgress(false)

      return true
    } catch (err) {
      // In case of retry, we need to request a new CSRF-token!
      MY_LS.clear()

      onDisplayMessage(
        new Message(
          MessageSeverity.ERROR,
          '',
          err.response.data?.amasisCode,
          err.response.data?.description
            ? err.response.data?.description
            : err.response.data.title
              ? err.response.data.title
              : err.response.data.message
                ? err.response.data.message
                : err.response.data.messages
                  ? err.response.data.messages[0].description
                  : err.response.data.error,
        ),
      )
      // Set timestamp in cache (to decide later on whether session has timed-out or not)
      LocalStorage.put([LocalStorage.KEY_TIMESTAMP_LAST_SERVER_CALL], new Date().getTime())
      setLoginInProgress(false)
      return false
    }
  }

  /**
   * Call server to check token (2nd step of MFA-authentication)
   */
  const handleLogIn2FA: SubmitHandler<Inputs> = async (data) => {
    try {
      const urlToCall = tools.dformat(UrlApis.URL_SPECIAL_VERIFYQR_POST_PARAMETERIZED, lang, data?.token)
      console.debug('2FA-signing in: ' + urlToCall)
      const headerCompleted = await AxiosUtils.getHeader(AxiosUtils.HEADERS.HEADER_SIGN_IN_OUT)
      const response = await axios.post(
        encodeURI(urlToCall),
        {},
        {
          headers: headerCompleted,
          withCredentials: false,
        },
      )
      console.debug('2FA-signing in response received!')

      // Login suceesful
      setIsConnected(true)
      postLoginSuccessful(response)

      console.debug('2FA-signing in finished !')
      // Set timestamp in cache (to decide later on whether session has timed-out or not)
      LocalStorage.put([LocalStorage.KEY_TIMESTAMP_LAST_SERVER_CALL], new Date().getTime())

      setLoginInProgress(false)

      return true
    } catch (err) {
      MY_LS.clear()
      setValue('token', '')
      setIsConnected(false)

      onDisplayMessage(
        new Message(
          MessageSeverity.ERROR,
          '',
          err.response.data?.amasisCode,
          err.response.data?.description
            ? err.response.data?.description
            : err.response.data.title
              ? err.response.data.title
              : err.response.data.message
                ? err.response.data.message
                : err.response.data.messages
                  ? err.response.data.messages[0].description
                  : err.response.data.error,
        ),
      )
      // Set timestamp in cache (to decide later on whether session has timed-out or not)
      LocalStorage.put([LocalStorage.KEY_TIMESTAMP_LAST_SERVER_CALL], new Date().getTime())
      setLoginInProgress(false)
      return false
    }
  }

  const performLogIn1FA = async (data) => {
    // Perform 1FA-log-in of the user
    setLoginInProgress(true)
    await handleLogIn1FA(data)
  }

  const performLogIn2FA = async (data) => {
    // Perform 2FA-log-in of the user
    setLoginInProgress(true)
    await handleLogIn2FA(data)
  }

  // TODO: remove when SSO ok
  function delay(milliseconds: number) {
    return new Promise((resolve) => {
      setTimeout(resolve, milliseconds)
    })
  }

  const handleLogInSsoStep1 = async () => {
    try {
      const redirect_url = window.location.href
      const urlToCall = tools.dformat(UrlApis.URL_SPECIAL_SSO_SIGNIN_GET, lang, redirect_url)
      console.debug('Signin with SSO activated (Step 1): ' + urlToCall)
      await delay(2000)
      const headerCompleted = await AxiosUtils.getHeader(AxiosUtils.HEADERS.HEADER_SIGN_IN_OUT)
      const response = await axios.get(encodeURI(urlToCall), {
        headers: headerCompleted,
        withCredentials: false,
      })
      console.debug('Signin with SSO (Step 1): response received!')
      /*    console.debug('redirect location is <' + response?.headers?.get('location') + '>')
      // Memorize into LS that Step-2 of SSO-process is being started now
      LocalStorage.put([LocalStorage.KEY_SSO_CONNECTION_IN_PROGRESS], true)
      window.location.href = response?.headers?.get('location')
      return true
      */
      setIsSsoLoginOk(true)
      setIsConnected(true)
      postLoginSuccessful(response)
      setLoginInProgress(false)
      console.debug('Signin with SSO finished !')
      // Set timestamp in cache (to decide later on whether session has timed-out or not)
      LocalStorage.put([LocalStorage.KEY_TIMESTAMP_LAST_SERVER_CALL], new Date().getTime())

      // TODO: confirm what to do in case the user is being editing/creating a document
      navigate(0)
    } catch (err) {
      // SSO-login Step-1 failed => normal login
      onDisplayMessage(new Message(MessageSeverity.WARNING, '', null, t('SSO_LOGIN_FAIL')))
      setIsSsoLoginOk(false)
      setLoginInProgress(false)
    }
  }

  const clearStates = () => {
    setIsMfaSoftLoginOk(false)
    setReturnLogin1FA(null)
    setValue('user', '')
    setValue('password', '')
    setValue('token', '')
    setShowPassword(false)
  }

  const handleCloseModal = () => {
    onCancelModal()
  }

  useEffect(() => {
    if (!isConnected) {
      if (isSsoActivated && isSsoLoginOk !== false) {
        setLoginInProgress(true)
        handleLogInSsoStep1()
      } else {
        // Manual login applies
      }
    }
  }, [isConnected, isSsoActivated])

  useEffect(() => {
    clearStates()
    reset()
  }, [isOpened])

  /**
   * En cas d'utilisateur non connecté, on a l'affichage des composants selon les 3 cas suivants:
   * - mode normal (sans MFA, sans SSO): affichage de la fenêtre de login avec saisie de MdP - correspond également au step-1 du MFA - si OK, on ferme la modale
   * - MFA activé: step-1 / affichage de la fenêtre de login avec saisie de MdP - step-2: affichage optionnel du QR-code et du token puis soumission - si OK, on ferme la modale
   * - SSO activé: en une seule étape, on appelle le companion pour se ,connecter via le SSO - si OK, on ferme la modale
   *
   * Algorithme simplifié:
   * ===
   * si SSO activé: appel du companion. Si OK, on rafraîchit la page, sinon on affiche un message d'erreur et on poursuit avec le mode de connexion manuel via la modale de login.
   * sinon :
   * si soit pas MFA, soit MFA non fini :	// Tous les autres cas (normal ou MFA-step-1)
   *   performLogIn1FA // step1 (login avec ou sans MFA avec saisie du MdP)
   *   sinon :	// MFA et step-1 OK
   * 	affichage [qr-code +] token à saisir
   * 	performLogIn2FA
   */

  const { getBackgroundColor } = useContext(LightDarkContext)

  return (
    <Modal
      className='modalLogin'
      open={isOpened}
      onClose={onCloseModal}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <div style={{ ...styleModal, backgroundColor: getBackgroundColor() }}>
        {loginInProgress && (
          <ProgressBar indeterminate style={{ position: 'absolute', top: 0, left: 0, width: '100%' }} />
        )}
        {!isConnected && isSsoActivated && isSsoLoginOk !== false ? (
          <div style={{ height: '4em', marginTop: '1.5em', marginLeft: '1em' }}>{t('sso_connect')}</div>
        ) : !isConnected ? (
          <FormProvider {...methods}>
            {!isMfaSoftLoginOk || !isMfaActivated ? (
              <form onSubmit={handleSubmit(performLogIn1FA)}>
                <Flex
                  direction='column'
                  alignItems='center'
                  childrenStyle={{ flex: 1 }}
                  style={{ marginTop: '1em', textAlign: 'left' }}
                >
                  <FormInput
                    name='user'
                    label={t('7000483')}
                    style={{
                      visibility: !isSsoActivated || (isSsoActivated && isSsoLoginOk === false) ? 'visible' : 'hidden',
                      margin: 1,
                      width: '30ch',
                    }}
                    capitalize
                    rules={{ required: true }}
                  />

                  <FormInput
                    type={showPassword ? 'text' : 'password'}
                    name='password'
                    showPassword={showPassword}
                    style={{
                      visibility: !isSsoActivated || (isSsoActivated && isSsoLoginOk === false) ? 'visible' : 'hidden',
                      margin: 1,
                      width: '30ch',
                    }}
                    setShowPassword={setShowPassword}
                    label={t('7000244')}
                    rules={{ required: true }}
                    endAdornment={
                      <IconButton
                        variant='ghost'
                        aria-label={showPassword ? 'hide the password' : 'display the password'}
                        disabled={isSsoActivated && isSsoLoginOk !== false}
                        onClick={() => setShowPassword((show) => !show)}
                        onMouseDown={(event) => event.preventDefault()}
                        onMouseUp={(event) => event.preventDefault()}
                        edge='end'
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    }
                  />
                  {/*  <div className='loginPassForgotten'>
                    <div />
                    <div>
                      <a
                        href='#'
                        className='linkCursor'
                        style={{ visibility: isSsoActivated && isSsoLoginOk !== false ? 'hidden' : 'inherit' }}
                      >
                        {t('9480012')}
                      </a>
                    </div> 
                  </div>*/}
                </Flex>

                <div className='loginButtons'>
                  <Button variant='secondary' onClick={handleCloseModal}>
                    {t('8000569')}
                  </Button>
                  <Button
                    type='submit'
                    variant='primary'
                    disabled={
                      (!methods.formState.isValid && !isSsoActivated) ||
                      loginInProgress ||
                      (isSsoActivated && isSsoLoginOk !== false)
                    }
                    loading={isLoading}
                    loadingPosition='start'
                  >
                    {t('8000202')}
                  </Button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleSubmit(performLogIn2FA)}>
                {returnLogin1FA?.hasOwnProperty(PROP_QRCODE_IMG) ? (
                  <div className='loginQrCode'>
                    <img src={returnLogin1FA[PROP_QRCODE_IMG]} alt='please scan QR-code for the 2FA-process' />
                  </div>
                ) : (
                  ''
                )}
                <div className='tokenSection'>
                  <FormInput
                    name='token'
                    label={t('9510043')}
                    style={{ margin: 1, width: '30ch' }}
                    rules={{ required: true }}
                  />
                </div>
                <br />

                <div className='loginButtons'>
                  <Button variant='secondary' onClick={handleCloseModal}>
                    {t('8000569')}
                  </Button>
                  <Button type='submit' variant='primary' disabled={loginInProgress}>
                    {t('8000202')}
                  </Button>
                </div>
              </form>
            )}

            <div className='logoCustomer'>
              <div
                style={{
                  background: `url(${mode === 'light' ? ImgBgLight : ImgBgDark}) no-repeat center center fixed`,
                  backgroundSize: '75%',
                  width: '100%',
                  height: '15rem',
                  backgroundColor: mode === 'light' ? 'white' : 'rgb(0, 32, 96)',
                }}
              />
            </div>
          </FormProvider>
        ) : (
          <div className='loginButtons'>
            <Button variant='secondary' onClick={handleCloseModal}>
              {t('8000569')}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default ModalLogIn
